import { InfoCircleIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { z } from 'zod';

import { hasMoreInfiniteItems } from '@/Pages/Conversation/utils/hasMoreInfiniteItems';
import { RunDetailsFilterBar } from '@/components/FilterBar/FilterBar';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { Button, Text } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useRun, useRunsInfinite } from '@/hooks/useSwr';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { WaterfallToggleButton } from '../Run/components/RunDetailsNavigation';
import RunDetailsNavigationHeader from '../Run/components/RunDetailsNavigationHeader';
import { RunDetailsPanel } from '../Run/components/RunDetailsPanel';
import { RunTreeSkeleton } from '../Run/components/RunTreeSkeleton';
import { ThreadWaterfallLatencyGraph } from '../Run/components/ThreadWaterfallLatencyGraph';
import { RunTreeNode } from '../Run/components/Tree';
import { SIDEBAR_LOCAL_STORAGE_KEY } from '../Run/components/constants';
import { useDetectXSPanel } from '../Run/utils/useDetectXSPanel';
import { useGroupChildByParentId } from '../Run/utils/useGroupChildByParentId';
import {
  useFilteredRunsInTreeStructure,
  useRunsInTreeStructure,
} from '../Run/utils/useRunsInTreeStructure';
import { useUserPrefs } from '../Run/utils/useUserPrefs';
import { ConversationTurnV2 } from './ConversationTurnV2';
import { useThreadChildRuns } from './utils/useThreadChildRuns';

export const CollatedRunTreeV2 = ({
  treeRef,
  isRootRunsLoading,
  rootRuns,
  selectedRun,
  onNavigate,
  threadChildRuns,
  searchFilter,
  setSearchFilter,
  isFilteredRunsLoading,
  filteredRuns,
  togglePanelSize,
  isXsPanel,
  panelId,
  hasMoreRuns,
  isNextPageValidating,
  getNextPage,
  onMouseEnter,
  onMouseLeave,
  showGraphStats,
  toggleGraphStats,
  activeTab,
}: {
  treeRef?: RefObject<HTMLDivElement>;
  isRootRunsLoading?: boolean;
  isFilteredRunsLoading?: boolean;
  rootRuns: RunSchema[];

  threadChildRuns: Record<string, RunSchema[]>;
  hasMoreRuns: boolean;
  isNextPageValidating: boolean;
  getNextPage: () => void;

  selectedRun?: RunSchema;
  onNavigate: (run: RunSchema | null) => void;
  searchFilter: string | undefined;
  setSearchFilter: (filter: string | undefined) => void;
  filteredRuns?: RunSchema[];

  togglePanelSize?: () => void;
  isXsPanel: boolean;
  panelId: string;
  onMouseEnter?: (run: RunSchema) => void;
  onMouseLeave?: () => void;
  showGraphStats: boolean;
  toggleGraphStats: (show: boolean) => void;
  activeTab: 'turn' | 'trace';
}) => {
  const scrollRef = useRef<VirtuosoHandle>(null);

  const [foldedRuns, setFoldedRuns] = useState<string[]>([]);

  useEffect(() => {
    if (activeTab === 'turn') {
      setFoldedRuns(rootRuns.map((r) => r.id));
    } else if (activeTab === 'trace') {
      setFoldedRuns((prev) => prev.filter((r) => r !== selectedRun?.id));
    }
  }, [activeTab, rootRuns, selectedRun]);

  const { filterCount, userAllRuns, setUserAllRuns, setUserFilteredOnly } =
    useUserPrefs({
      searchFilter,
      filteredRuns,
    });

  const unfilteredTree = useRunsInTreeStructure({
    rootRuns,
    run: selectedRun,
    childRunLookup: threadChildRuns,
    filteredRuns,
    // Filtered only is not supported for conversation view, so always show as false
    filteredOnly: false,
    userAllRuns,
    foldedRuns,
  });

  const filteredTree = useFilteredRunsInTreeStructure({
    filteredRuns,
    rootRuns,
    selectedRun,
    foldedRuns,
  });

  const { tree, hiddenCount, childRunLookupWithFolds } = searchFilter
    ? filteredTree
    : unfilteredTree;

  const { flattenedRuns, depthMetaList } = tree;

  const allRuns = userAllRuns || hiddenCount === 0;

  const visibility = allRuns ? 'all' : 'most_relevant';

  const setVisibility = (value: 'all' | 'filtered' | 'most_relevant') => {
    setUserFilteredOnly(value === 'filtered');
    setUserAllRuns(value === 'all');
    if (value === 'all') {
      setSearchFilter(undefined);
    }
  };

  const hideTitles = useDetectXSPanel({ panelId, breakpoint: 130 });
  const isLoading = searchFilter ? isFilteredRunsLoading : isRootRunsLoading;

  const treeOrGraph = showGraphStats ? (
    <ThreadWaterfallLatencyGraph
      rootRuns={rootRuns}
      selectedRun={selectedRun}
      onNavigate={onNavigate}
      filteredRuns={filteredRuns}
      foldedRuns={foldedRuns}
      setFoldedRuns={setFoldedRuns}
      unfilteredTree={unfilteredTree}
    />
  ) : (
    <Virtuoso
      ref={scrollRef}
      data={flattenedRuns}
      className="[&_[data-viewport-type=element]>div:last-of-type]:sticky [&_[data-viewport-type=element]>div:last-of-type]:bottom-0"
      itemContent={(
        idx,
        { depth, run, isActiveDescendant, rootRunId, conversationIdx }
      ) => {
        const childRuns = childRunLookupWithFolds?.[run.id];
        const rootRun = rootRuns.find(
          (i) => i.id === rootRunId || i.id === run.id
        ) as RunSchema;

        return (
          <RunTreeNode
            scrollIdx={idx}
            scrollRef={scrollRef}
            run={run}
            depth={depth}
            depthMeta={depthMetaList[idx]}
            activeRunId={selectedRun?.id}
            rootRun={rootRun}
            isActiveDescendant={isActiveDescendant}
            isPublicPage={false}
            onClick={(r) => onNavigate(selectedRun?.id === r.id ? null : r)}
            setFoldedRuns={setFoldedRuns}
            hasChildren={Array.isArray(childRuns)}
            hasChildrenVisible={
              Array.isArray(childRuns) && childRuns.length > 0
            }
            isFlatStructure={isXsPanel}
            showRootExpandToggle={activeTab === 'trace'}
            conversationIdx={conversationIdx}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            hideTitles={hideTitles}
            focused={!!filteredRuns?.find((i) => i.id === run.id)}
            rootRunFormat={'thread'}
          />
        );
      }}
      components={{}}
    />
  );

  if (isLoading) {
    return <RunTreeSkeleton isXsPanel={isXsPanel} />;
  }
  return (
    <div ref={treeRef} className="flex h-full flex-col" id={panelId}>
      <RunDetailsNavigationHeader
        hiddenCount={hiddenCount}
        filterCount={filterCount}
        visibility={visibility}
        setVisibility={setVisibility}
        hideFilteredVisibility={true}
        foldedRuns={foldedRuns}
        toggleFoldAllRuns={
          activeTab === 'trace'
            ? () => {
                if (foldedRuns.length > 0) {
                  setFoldedRuns([]);
                } else {
                  setFoldedRuns(rootRuns.map((r) => r.id));
                }
              }
            : undefined
        }
        childRunLookup={threadChildRuns}
        insidePane={false}
        isXsPanel={isXsPanel}
        togglePanelSize={togglePanelSize}
        additionalFilters={
          <div className={cn('flex', isXsPanel ? 'flex-col' : 'gap-1')}>
            <RunDetailsFilterBar
              rootRuns={rootRuns}
              onChange={({ filter }) => setSearchFilter(filter)}
              value={{ filter: searchFilter }}
              buttonVariant={isXsPanel ? 'inline' : 'button'}
            />

            <WaterfallToggleButton
              showGraphStats={showGraphStats}
              toggleGraphStats={toggleGraphStats}
              isXsPanel={isXsPanel}
              disabled={activeTab === 'turn'}
            />
          </div>
        }
        isPublicPage={false}
        headerLabel="Turns"
      />
      {!flattenedRuns.length ? (
        <div className="flex items-center gap-2 p-4">
          <InfoCircleIcon className="h-4 w-4 flex-shrink-0" />
          <Text variant="sm" as="span">
            No runs found {searchFilter && 'for selected filter'}
          </Text>
          {searchFilter && (
            <Button
              variant="underlined"
              color="secondary"
              size="sm"
              onClick={() => setSearchFilter(undefined)}
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        treeOrGraph
      )}
      {hasMoreRuns && (rootRuns.length ?? 0) > 0 && (
        <LoadMoreButton
          isInitialLoad
          isLoading={isNextPageValidating}
          onClick={getNextPage}
          minimized={isXsPanel}
          isVirtual={true}
        />
      )}
    </div>
  );
};

export function CollatedConversationV2({
  rootRunsQuery,
  plainOptions,
  filterRuns,
  searchFilter,
  setSearchFilter,
  sessionId,
  selectedRunId,
  setSelectedRunId,
  activeTab,
}: {
  rootRunsQuery: ReturnType<typeof useRunsInfinite>;
  plainOptions: {
    input:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
    output:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
  };
  filterRuns: ReturnType<typeof useRunsInfinite>;
  searchFilter: string | undefined;
  setSearchFilter: (filter: string | undefined) => void;
  sessionId?: string;
  selectedRunId: string | null;
  setSelectedRunId: (id: string | null) => void;
  activeTab: 'turn' | 'trace';
}) {
  const runFallbackRef = useRef<Record<string, RunSchema>>({});

  const rootRuns = useMemo(
    () =>
      rootRunsQuery.data?.flat().map((r) => ({ ...r, run_type: 'thread' })) ??
      [],
    [rootRunsQuery.data]
  );

  useEffect(() => {
    rootRuns.forEach((run) => {
      runFallbackRef.current[run.id] = run;
    });
  }, [rootRuns]);

  const fallbackData = selectedRunId
    ? runFallbackRef.current[selectedRunId]
    : undefined;

  const { data: selectedRun, isLoading: isRunLoading } = useRun({
    // if selectedRun is found in root run list, don't refetch it
    id: selectedRunId,
    options: {
      fallbackData: fallbackData,
      keepPreviousData: !fallbackData,
    },
    withContents: true,
    excludeSerialized: true,
    sessionId,
  });

  const { data: threadChildRuns, isLoading: isChildRunsLoading } =
    useThreadChildRuns({
      selectedRootRun: activeTab === 'trace' ? selectedRun : undefined,
    });

  const threadFilteredChildRuns = useGroupChildByParentId(
    filterRuns.data?.flat(),
    filterRuns.isLoading
  );

  const [sidebarVisible, setSidebarVisible] = useLocalStorageState(
    SIDEBAR_LOCAL_STORAGE_KEY,
    true
  );

  const hasMoreFilteredRuns = hasMoreInfiniteItems(filterRuns);
  const hasMoreRuns = hasMoreInfiniteItems(rootRunsQuery);

  const isNextPageValidating =
    (filterRuns.isValidating &&
      filterRuns.size > 1 &&
      filterRuns.data?.length != filterRuns.size) ||
    (rootRunsQuery.isValidating &&
      rootRunsQuery.size > 1 &&
      rootRunsQuery.data?.length != rootRunsQuery.size);

  const panelRef = useRef<ImperativePanelHandle>(null);

  const togglePanelSize = () => {
    const prevSize = panelRef.current?.getSize();
    if (prevSize) {
      panelRef.current?.resize(prevSize > 6 ? 6 : 30);
    }
  };
  const panelId = 'trace-panel';
  const isXsPanel = useDetectXSPanel({ panelId });

  const selectedRunIndex = rootRuns.findIndex((run) => {
    return selectedRunId === run.id;
  });

  const { value: enableThreadViewPlayground } = useOrgConfig(
    OrgConfigs.enable_thread_view_playground
  );

  const priorThreadRunIds = !enableThreadViewPlayground
    ? undefined
    : selectedRunIndex !== -1
    ? rootRuns
        .slice(Math.max(0, selectedRunIndex - 9), selectedRunIndex)
        .map((run) => run.id)
    : [];

  const [showGraphStats, setShowGraphStats] = useLocalStorageState(
    `ls:threadDetails:showGraphStats`,
    false,
    z.boolean()
  );

  const toggleGraphStats = (show: boolean) => {
    setShowGraphStats(show);
    setTimeout(() => {
      panelRef.current?.resize(show ? 50 : 40);
    }, 0);
  };

  const conversationVirtuosoRef = useRef<VirtuosoHandle>(null);

  // Scroll to selected run when it changes
  useEffect(() => {
    if (selectedRunId && activeTab === 'turn') {
      const selectedIndex = rootRuns.findIndex(
        (run) => run.id === selectedRunId
      );
      if (selectedIndex !== -1) {
        // Use a small delay to ensure the virtuoso has rendered the items
        setTimeout(() => {
          conversationVirtuosoRef.current?.scrollToIndex({
            index: selectedIndex,
            align: 'start',
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }, [selectedRunId, activeTab, rootRuns]);

  return (
    <div className="flex w-full flex-1 flex-col overflow-hidden">
      <PanelGroup
        autoSaveId={`collated-conversation`}
        direction="horizontal"
        className="flex-grow"
      >
        <Panel
          ref={panelRef}
          minSize={6}
          defaultSize={40}
          className="relative flex min-w-[115px] flex-col"
        >
          {(isChildRunsLoading || filterRuns?.isLoading) && (
            <div className="pointer-events-none absolute inset-x-0 top-0 opacity-100">
              <LinearProgress />
            </div>
          )}
          {rootRunsQuery.data && (
            <CollatedRunTreeV2
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              filteredRuns={filterRuns?.data?.flat()}
              rootRuns={rootRuns}
              selectedRun={selectedRunId ? selectedRun : undefined}
              threadChildRuns={
                searchFilter && threadFilteredChildRuns
                  ? threadFilteredChildRuns
                  : threadChildRuns
              }
              onNavigate={(run) => {
                if (selectedRun) {
                  runFallbackRef.current[selectedRun?.id] = selectedRun;
                }
                setSelectedRunId(run?.id ?? null);
              }}
              togglePanelSize={togglePanelSize}
              isXsPanel={isXsPanel}
              panelId="trace-panel"
              hasMoreRuns={searchFilter ? hasMoreFilteredRuns : hasMoreRuns}
              isNextPageValidating={isNextPageValidating}
              getNextPage={() => {
                if (searchFilter) {
                  filterRuns.setSize((size) => size + 1);
                } else {
                  rootRunsQuery.setSize((size) => size + 1);
                }
              }}
              showGraphStats={showGraphStats}
              toggleGraphStats={toggleGraphStats}
              activeTab={activeTab}
            />
          )}
        </Panel>
        <PanelResizeHandle className="group relative z-10 flex items-stretch text-secondary data-[resize-handle-active]:text-brand-primary">
          <div className="w-[1px] bg-tertiary" />
          <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
          <div className="absolute inset-y-0 w-[2px] bg-brand-primary opacity-0 transition-all group-hover:opacity-100" />
        </PanelResizeHandle>
        <Panel className="flex min-w-0 flex-col !overflow-visible">
          {!selectedRunId || activeTab === 'turn' ? (
            <div
              className="flex flex-1 flex-col overflow-y-auto border-b border-secondary"
              data-testid="conversation-overview"
            >
              <Virtuoso
                ref={conversationVirtuosoRef}
                totalCount={rootRuns.length}
                data={rootRuns}
                itemContent={(idx, run) => {
                  return (
                    <ConversationTurnV2
                      run={run}
                      idx={idx}
                      plainOptions={plainOptions}
                      rootRuns={rootRuns}
                      selectedRunId={selectedRunId}
                    />
                  );
                }}
                components={{
                  Footer: () => {
                    return (
                      hasMoreRuns &&
                      rootRuns.length > 0 && (
                        <div className="flex w-full items-center justify-center border-t border-secondary">
                          <LoadMoreButton
                            isInitialLoad
                            isLoading={isNextPageValidating}
                            onClick={() =>
                              rootRunsQuery.setSize((size) => size + 1)
                            }
                          />
                        </div>
                      )
                    );
                  },
                }}
              />
            </div>
          ) : (
            <>
              {isRunLoading && (
                <div>
                  <LinearProgress />
                </div>
              )}
              <div
                className={cn('mt-4 overflow-y-auto', isRunLoading && 'mt-2.5')}
              >
                <RunDetailsPanel
                  run={selectedRun}
                  isRunLoading={isRunLoading}
                  isPublicPage={false}
                  priorThreadRunIds={priorThreadRunIds}
                  sidebarVisible={sidebarVisible}
                  setSidebarVisible={setSidebarVisible}
                />
              </div>
            </>
          )}
        </Panel>
      </PanelGroup>
    </div>
  );
}
